import React from 'react';
import Layout from '@/components/Layout';

const Section = ({ number, title, children }) => (
  <section>
    <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--color-text-primary)' }}>
      {number}. {title}
    </h2>
    <div className="space-y-3">{children}</div>
  </section>
);

const PrivacyPolicyPage = () => {
  return (
    <Layout>
      <div style={{ background: 'var(--color-background)' }} className="section-padding">
        <div className="container-custom max-w-4xl">
          <h1 className="text-5xl font-bold mb-4" style={{ color: 'var(--color-text-primary)' }}>
            Privacy Policy
          </h1>
          <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>
            Last updated: 10 October 2026
          </p>
          <p className="mb-10 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
            This Privacy Policy explains how Bamo Tech Sphere ("BTS", "we", "us", "our") collects, uses,
            shares, and protects personal data when you use bamotechsphere.com and related services (the
            "Service"). We are a "data controller" for the purposes of Kenya's Data Protection Act, 2019
            ("the DPA") and apply equivalent safeguards for visitors covered by other data protection laws,
            including the EU/UK General Data Protection Regulation ("GDPR") where applicable.
          </p>

          <div className="space-y-10" style={{ color: 'var(--color-text-secondary)' }}>
            <Section number="1" title="Who we are and how to reach us">
              <p>
                Bamo Tech Sphere is based in Nairobi, Kenya. For any question or request relating to this
                Policy, contact us at:
              </p>
              <p>
                Email: aethernexus05@gmail.com
                <br />
                Phone: +254 703 207 300
              </p>
              <p className="text-sm italic">
                [Full registered office address and, once appointed, the name/contact details of a formal
                Data Protection Officer under DPA s.24 to be inserted here. Registration with the Office of
                the Data Protection Commissioner (ODPC) as a data controller/processor should be completed
                and the registration number added here before full public launch — see section 11.]
              </p>
            </Section>

            <Section number="2" title="Information we collect">
              <p>We collect the following categories of personal data:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><span style={{ color: 'var(--color-text-primary)' }}>Identity and contact data</span> — name, email address, phone number, delivery and billing address</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Account data</span> — login credentials (passwords are stored hashed, never in plain text), order history, wishlist, and saved preferences</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Transaction data</span> — order details, amounts, and payment status. Full card numbers and mobile money PINs are processed directly by our licensed payment partners and are never stored on our servers</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Communications</span> — messages you send us via email, WhatsApp, or support channels, and reviews you post</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Technical data</span> — IP address, browser type, device identifiers, and session cookies used to keep you logged in and secure your session</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Sign-in data</span> — if you use Google or Facebook sign-in, we receive your name, email address, and profile picture from that provider, as permitted by your settings with them</li>
              </ul>
            </Section>

            <Section number="3" title="How and why we use your information (legal basis)">
              <p>
                Under the DPA, we must have a valid legal basis for every use of your personal data. We rely
                on the following:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><span style={{ color: 'var(--color-text-primary)' }}>Performance of a contract</span> — to create your account, process and deliver your orders, handle payments, returns, and warranty claims</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Legal obligation</span> — to keep records required for tax (KRA), consumer protection, and anti-fraud purposes</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Legitimate interest</span> — to keep the Service secure, prevent fraud, improve our products and site performance, and understand aggregate sales trends, balanced against your right to privacy</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Consent</span> — for marketing emails/SMS and optional AI-personalised recommendations, which you can withdraw at any time</li>
              </ul>
            </Section>

            <Section number="4" title="Cookies and similar technology">
              <p>
                We use strictly necessary cookies (e.g. your session login cookie) to operate the Service, and
                may use analytics cookies to understand site usage. You can control or disable non-essential
                cookies through your browser settings; disabling strictly necessary cookies may prevent you
                from logging in or completing a purchase.
              </p>
            </Section>

            <Section number="5" title="Who we share your information with">
              <p>We do not sell your personal data. We share it only as needed with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li><span style={{ color: 'var(--color-text-primary)' }}>Payment processors</span> — Stripe, PayPal, Skrill, and our M-Pesa payment aggregator, to complete and verify transactions</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Media and infrastructure providers</span> — including Cloudinary for image/video hosting and our cloud database/hosting providers, under data processing terms</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Email providers</span> — Resend, to send order confirmations and account emails</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Identity providers</span> — Google/Facebook, only if you choose to sign in with them</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Delivery and logistics partners</span> — to fulfil and track your order</li>
                <li><span style={{ color: 'var(--color-text-primary)' }}>Regulators and law enforcement</span> — where we are legally required to disclose information, including to the ODPC, KRA, or a court of competent jurisdiction</li>
              </ul>
              <p>
                Each third party is only given the information it needs to perform its function and is
                contractually required to protect it.
              </p>
            </Section>

            <Section number="6" title="International data transfers">
              <p>
                Some of our service providers (such as cloud hosting and payment processors) may process data
                outside Kenya, including in the United States or European Union. Where this happens, we rely
                on the safeguards permitted under DPA s.48, which include the receiving country having
                adequate data protection, or the transfer being subject to a contract that imposes obligations
                materially similar to the DPA, such as standard contractual clauses. If you access the Service
                from the EU/UK, your data may be transferred to Kenya or other countries outside the EU/UK; we
                take reasonable steps to ensure it receives an equivalent standard of protection.
              </p>
            </Section>

            <Section number="7" title="Data retention">
              <p>
                We keep personal data only as long as necessary for the purposes described in this Policy —
                typically for the lifetime of your account plus a period afterward to meet tax, accounting,
                and legal record-keeping obligations (generally up to 7 years for financial records under
                Kenyan tax law), after which it is deleted or anonymised.
              </p>
            </Section>

            <Section number="8" title="Data security">
              <p>
                We apply technical and organisational measures appropriate to the risk, including encrypted
                connections (HTTPS), hashed password storage, access controls, and working only with payment
                partners that meet recognised card-industry security standards (PCI DSS). No system is
                completely secure, and we encourage you to use a strong, unique password for your account.
              </p>
            </Section>

            <Section number="9" title="Your rights">
              <p>Under the DPA (and GDPR, where it applies to you), you have the right to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Be informed about how your data is processed (this Policy)</li>
                <li>Access the personal data we hold about you</li>
                <li>Request correction of inaccurate or incomplete data</li>
                <li>Request deletion of your data, subject to our legal retention obligations</li>
                <li>Object to or restrict certain processing, including direct marketing</li>
                <li>Request a portable copy of data you provided to us</li>
                <li>Withdraw consent at any time, where processing is based on consent</li>
                <li>Lodge a complaint with the Office of the Data Protection Commissioner (ODPC) at odpc.go.ke, or your own country's data protection authority if outside Kenya</li>
              </ul>
              <p>
                To exercise any of these rights, contact us at aethernexus05@gmail.com. We will respond within
                the timeframe required by law (generally within one month under the DPA).
              </p>
            </Section>

            <Section number="10" title="Children's privacy">
              <p>
                The Service is not directed at children under 18. We do not knowingly collect personal data
                from children without the consent of a parent or guardian. If you believe a child has provided
                us with personal data without appropriate consent, please contact us so we can delete it.
              </p>
            </Section>

            <Section number="11" title="Regulatory registration">
              <p>
                Under the DPA, businesses that process personal data in Kenya above the prescribed thresholds
                are required to register with the ODPC as a data controller and/or data processor.
              </p>
              <p className="text-sm italic">
                [Insert ODPC registration number here once registration is complete, at odpc.go.ke.]
              </p>
            </Section>

            <Section number="12" title="Changes to this Policy">
              <p>
                We may update this Privacy Policy to reflect changes in our practices or the law. The "Last
                updated" date above will change accordingly, and we will notify registered users of material
                changes by email or a notice on the Service.
              </p>
            </Section>

            <Section number="13" title="Contact us">
              <p>
                If you have questions about this Privacy Policy or wish to exercise your data rights:
                <br />
                Email: aethernexus05@gmail.com
                <br />
                Phone: +254 703 207 300
                <br />
                Registered address: Nairobi, Kenya
              </p>
            </Section>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicyPage;
